import { useEffect } from "react";
import {
  fetchFormByLabTestId,
  fetchOrderLanguagePatient,
  addTherapistForClient,
  submitSiboForm,
} from "../../../Services/apiService";

const LANGUAGE_CACHE_PREFIX = "sibo_language_";
const DRAFT_CACHE_PREFIX = "sibo_form_draft_";

export const useFormLifecycle = ({
  labTestId,
  formData,
  setFormData,
  setOrderLanguage,
  loadingFormData,
  setLoadingFormData,
  setSnackbar,
  loadInitialTherapists,
  t,
}) => {
  // Restore cached language immediately (prevents wrong-language flash on refresh)
  useEffect(() => {
    if (!labTestId) return;
    const cached = sessionStorage.getItem(`${LANGUAGE_CACHE_PREFIX}${labTestId}`);
    if (cached) setOrderLanguage(cached);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Load form data and initial therapists on mount
  useEffect(() => {
    const loadFormData = async () => {
      if (!labTestId) {
        setFormData((prev) => ({
          ...prev,
          orderNumber: "--",
          testIdentifier: "--",
          testSubstrate: "--",
        }));
        return;
      }

      try {
        setLoadingFormData(true);
        const response = await fetchFormByLabTestId(labTestId);
        if (response?.data) {
          const d = response.data;
          setFormData((prev) => ({
            ...prev,
            orderNumber: d.order_number || "",
            testIdentifier: d.test_identifier || "",
            testSubstrate: d.substrate || "",
            labTestId: d.lab_test_id || labTestId,
            progress: d.progress || "",
            typeOfTest: d.title || prev.typeOfTest,
          }));
          setSnackbar({ open: true, message: t.formDataLoaded, severity: "success" });
        } else {
          setSnackbar({ open: true, message: t.errorLoadingForm, severity: "error" });
        }
      } catch {
        setSnackbar({ open: true, message: t.errorLoadingForm, severity: "error" });
      } finally {
        setLoadingFormData(false);
      }
    };

    loadFormData();
    loadInitialTherapists();
  }, [labTestId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch order language when order number becomes available
  useEffect(() => {
    if (!formData.orderNumber || formData.orderNumber === "--") return;

    const getOrderLanguage = async () => {
      try {
        const data = await fetchOrderLanguagePatient(formData.orderNumber);
        // API may return a string or an object; extract safely
        const language =
          typeof data === "string" ? data : (data?.order_language || "");

        if (language) {
          setOrderLanguage(language);
          // Persist so language is correct immediately on next visit/refresh
          const cacheKey = labTestId || formData.orderNumber;
          if (cacheKey) {
            sessionStorage.setItem(`${LANGUAGE_CACHE_PREFIX}${cacheKey}`, language);
          }
        }
      } catch {
        // Keep whatever language is already set (possibly from cache)
      }
    };

    getOrderLanguage();
  }, [formData.orderNumber]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load saved draft from localStorage when identifiers are available
  useEffect(() => {
    const { labTestId: ltid, orderNumber } = formData;
    if (!(ltid || orderNumber) || loadingFormData) return;

    const storageKey = ltid || orderNumber;
    try {
      const saved = localStorage.getItem(`${DRAFT_CACHE_PREFIX}${storageKey}`);
      if (saved) {
        const { lastSaved, version, ...clean } = JSON.parse(saved);
        setFormData((prev) => ({ ...prev, ...clean }));
        setSnackbar({
          open: true,
          message: `${t.draftLoaded} ${new Date(lastSaved).toLocaleString()}`,
          severity: "info",
        });
      }
    } catch {
      // Ignore malformed draft
    }
  }, [formData.labTestId, formData.orderNumber, loadingFormData]); // eslint-disable-line react-hooks/exhaustive-deps
};

export const buildSubmitPayload = (formData, therapistOptions) => ({
  test_information: {
    order_number: formData.orderNumber,
    test_identifier: formData.testIdentifier,
    title: formData.typeOfTest,
    substrate: formData.testSubstrate,
    lab_test_id: formData.labTestId,
  },
  mandatory_information: {
    first_name: formData.firstName,
    last_name: formData.lastName,
    date_of_birth: formData.dateOfBirth,
  },
  additional_information: {
    InitialTest: {
      InitialFindings: formData.initialFindings,
      followup_test: formData.followUpTest,
    },
    dailySymptoms: { ...formData.dailySymptoms },
  },
  therapist: (() => {
    if (formData.otherTherapist) {
      return {
        other: true,
        therapist_id: null,
        therapist_name: formData.therapistName || "",
        practice_name: formData.practiceName || "",
        city: formData.city || "",
        email: formData.email || "",
      };
    }
    if (formData.selectedTherapist && formData.selectedTherapist !== "other") {
      const th =
        formData.selectedTherapistData ||
        therapistOptions.find((t) => t.id === formData.selectedTherapist);
      return {
        other: false,
        therapist_id: formData.selectedTherapist,
        therapist_name: th?.name || "",
        practice_name: th?.practice || "",
        city: th?.city || "",
        email: th?.email || "",
      };
    }
    return { other: false, therapist_id: null, therapist_name: "", practice_name: "", city: "", email: "" };
  })(),
  additional_email: {
    emails: (formData.additionalEmails || [])
      .map((e) => (e || "").trim())
      .filter(Boolean),
  },
  testSymptoms: {
    bloating: { checked: formData.testSymptoms.bloating.checked, minutes: formData.testSymptoms.bloating.minutes },
    diarrhoea: { checked: formData.testSymptoms.diarrhoea.checked, minutes: formData.testSymptoms.diarrhoea.minutes },
    abdominalPain: { checked: formData.testSymptoms.abdominalPain.checked, minutes: formData.testSymptoms.abdominalPain.minutes },
    others: {
      checked: formData.testSymptoms.others.checked,
      symptoms: formData.testSymptoms.others.symptoms.map(({ description, minutes, wordCount }) => ({
        description, minutes, wordCount,
      })),
    },
  },
  consentDeclaration: formData.consentDeclaration,
  dataUsageAgreement: formData.dataUsageAgreement,
  submissionTimestamp: new Date().toISOString(),
  formVersion: "1.0",
});

export const confirmSaveDraft = ({ formData, setSnackbar, setSaveDialogOpen, t }) => {
  try {
    const storageKey = formData.labTestId || formData.orderNumber || "default";
    localStorage.setItem(
      `${DRAFT_CACHE_PREFIX}${storageKey}`,
      JSON.stringify({ ...formData, lastSaved: new Date().toISOString(), version: 1 })
    );
    setSnackbar({ open: true, message: t.formSavedSuccessfully, severity: "success" });
  } catch {
    setSnackbar({ open: true, message: t.errorSavingForm, severity: "error" });
  }
  setSaveDialogOpen(false);
};

export const confirmSubmitForm = async ({
  formData,
  therapistOptions,
  setSnackbar,
  setSubmitDialogOpen,
  setIsSubmitting,
  onSubmitSuccess,
  t,
}) => {
  setIsSubmitting?.(true);
  try {
    if (formData.otherTherapist) {
      await addTherapistForClient({
        lab_test_id: formData.labTestId,
        therapist_name: formData.therapistName || "",
        practice_name: formData.practiceName || "",
        city: formData.city || "",
        therapist_email: formData.email || "",
      });
    }

    const payload = buildSubmitPayload(formData, therapistOptions);
    await submitSiboForm(payload);

    const storageKey = formData.labTestId || formData.orderNumber || "default";
    localStorage.removeItem(`${DRAFT_CACHE_PREFIX}${storageKey}`);

    onSubmitSuccess?.();
  } catch {
    setSnackbar({ open: true, message: t.errorSubmittingForm, severity: "error" });
  } finally {
    setIsSubmitting?.(false);
    setSubmitDialogOpen(false);
  }
};
